import { ESdChangeReason, EUserAccessPart, IHelpdeskLimits, ViewPart } from "@kuika/kuika-cl-model";
import { ReactNode } from "react";
import { HelpdeskModalView } from "../../../contexts/screen-design-context/provider-partitions/helpdesk-provider";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import HelpdeskProviderSwitch from "../helpdesk/helpdesk-provider-switch";
import { ItsmHelpdeskConnected } from "../helpdesk/itsm-helpdesk-connected";
import SearchablePanelHeader from "../left-panel/searchable-panel-header/searchable-panel-header";
import { HelpdeskStatusBoard } from "../zendesk/helpdesk-status-board";
import { ZendeskCanvas } from "../zendesk/zendesk-canvas";
import { ZendeskLeftPanel } from "../zendesk/zendesk-left-panel";
import "./helpdesk-modal.scss";

/** The two views in their on-screen order, so a view slides in from the side its tab lies on. */
const HELPDESK_VIEW_ORDER: HelpdeskModalView[] = ["inbox", "board"];

export interface IHelpdeskModalState {
  searchText: string;
  isSearchActive: boolean;
}

export interface IHelpdeskModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Kuika ITSM helpdesk only: open on the new-ticket form. */
  startWithNewTicket?: boolean;
}

class HelpdeskModal extends PureSdComponent<
  IHelpdeskModalProps & IWithSDProps & IWithContextProp,
  IHelpdeskModalState
> {
  constructor(props: IHelpdeskModalProps & IWithSDProps & IWithContextProp) {
    super(props);
    // SearchablePanelHeader writes what you type into the shared per-panel search store, and
    // the docked ticket panel restores from it on mount. Seed from the same store so the two
    // agree: the modal used to write the docked panel's search text and never read it back,
    // which left the docked list silently filtered by something typed in the modal.
    const storedSearchText: string = props.sd.searchTexts?.[ViewPart.ZendeskPanel] ?? "";

    this.state = {
      searchText: storedSearchText,
      isSearchActive: storedSearchText.length > 0
    };
  }

  componentDidMount(): void {
    // The modal mounts fresh on every open — `HelpdeskButton` unmounts it entirely on close —
    // so this is the one-shot "Helpdesk just opened" moment. See `openHelpdesk`'s docstring for
    // why the reset lives here and not in `ZendeskCanvas`.
    this.props.sd.openHelpdesk();
  }

  private onSearchTextChange = (searchText: string): void => {
    this.setState({ searchText });
  };

  private clearSearchText = (): void => {
    this.setState({ searchText: "", isSearchActive: false });
  };

  private startNewTicket = (): void => {
    // Writing happens in Inbox — the board has no composer — so creating from either view
    // lands in the same place.
    this.props.sd.setHelpdeskModalView("inbox");
    this.props.sd.onZendeskTicketCreate(true);
    this.props.sd.onZendeskTicketSelect(null);
  };

  private openTicketFromBoard = (ticketId: number): void => {
    this.props.sd.setHelpdeskModalView("inbox");
    this.props.sd.selectTicket(ticketId);
  };

  /**
   * Same gate the docked ticket panel applies. The backend enforces it too, but a create
   * control a user's project role does not allow should not be on screen either.
   */
  private canCreateTicket = (): boolean =>
    KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateNewTicket,
      this.props.sd.project?.projectAccess
    );

  private renderNewTicketButton = (): ReactNode => {
    if (!this.canCreateTicket()) return undefined;

    return (
      <KuikaButton
        data-testid="newTicketButton"
        size="md"
        disabled={this.props.sd.isZendeskTicketCreating}
        onClick={this.startNewTicket}
      >
        <KuikaSvg name="plus" width={16} height={16} />
        New ticket
      </KuikaButton>
    );
  };

  /** One view under the Inbox / Board tabs; KuikaTabContent draws the leaving one from its own key. */
  private renderView = (paneView: string): ReactNode => {
    if (paneView === "inbox") {
      return this.renderInbox();
    }
    return (
      <HelpdeskStatusBoard
        searchText={this.state.searchText}
        canCreateTicket={this.canCreateTicket()}
        onCreateTicket={this.startNewTicket}
        onSelectTicket={this.openTicketFromBoard}
      />
    );
  };

  private renderInbox = (): ReactNode => (
    <div className="kuika-helpdesk-modal__inbox">
      <div className="kuika-helpdesk-modal__list">
        <SearchablePanelHeader
          searchText={this.state.searchText}
          onSearchTextChange={this.onSearchTextChange}
          onSearchClearCallback={this.clearSearchText}
          viewPart={ViewPart.ZendeskPanel}
          panelName="Tickets"
          isSearchActive={this.state.isSearchActive}
          setSearchActive={(isSearchActive: boolean) => this.setState({ isSearchActive })}
          searchInputTestId="searchTicketInput"
          searchButtonTestId="searchTicketButton"
        />
        <div className="kuika-helpdesk-modal__list-body">
          <ZendeskLeftPanel searchText={this.state.searchText} />
        </div>
      </div>
      <div className="kuika-helpdesk-modal__canvas">
        <ZendeskCanvas />
      </div>
    </div>
  );

  private renderItsmHelpdesk = (limits: IHelpdeskLimits): ReactNode => (
    <ItsmHelpdeskConnected limits={limits} startWithNewTicket={this.props.startWithNewTicket} />
  );

  private renderZendeskHelpdesk = (): ReactNode => {
    const view: HelpdeskModalView = this.props.sd.helpdeskModalView;

    // Everything above the two view bodies is fixed: the modal's own header, this toolbar,
    // and — inside Inbox — the ticket list's header. Only the list and the ticket area
    // scroll, so the panel never moves out from under the pointer.
    return (
      <div className="kuika-helpdesk-modal__body">
        <div className="kuika-helpdesk-modal__toolbar">
          <KuikaTabs
            dataTestId="helpdeskViewTabs"
            size="sm"
            value={view}
            onChange={(value: string) => this.props.sd.setHelpdeskModalView(value as HelpdeskModalView)}
            options={[
              { value: "inbox", label: "Inbox", dataTestId: "helpdeskInboxTab" },
              { value: "board", label: "Board", dataTestId: "helpdeskBoardTab" }
            ]}
          />
          <span className="kuika-helpdesk-modal__toolbar-spacer" />
          {/*
            One create control for both views, on the toolbar. It used to sit in the ticket
            list's header, which hides its right-hand slot whenever search is active — so
            searching for a ticket removed the only way to open a new one.
          */}
          {this.renderNewTicketButton()}
        </div>
        <KuikaTabContent
          className="kuika-helpdesk-modal__views"
          activeKey={view}
          order={HELPDESK_VIEW_ORDER}
          render={this.renderView}
        />
      </div>
    );
  };

  render() {
    return (
      <KModal
        showModal={this.props.isOpen}
        closeModal={this.props.onClose}
        header="Helpdesk"
        width="calc(100vw - 2 * var(--gap-xl))"
        className="kuika-helpdesk-modal"
        bodyStyle={{ padding: 0 }}
      >
        <HelpdeskProviderSwitch itsm={this.renderItsmHelpdesk} zendesk={this.renderZendeskHelpdesk()} />
      </KModal>
    );
  }

  /**
   * Renders on any change that reaches it. Cheap: this component is mounted only while the
   * modal is open, and KModal destroys its children on close.
   */
  checkNeedRender = (): boolean => {
    return true;
  };
}

const helpdeskModal = withContext<IHelpdeskModalProps>(withSD<IHelpdeskModalProps>(HelpdeskModal));
export { helpdeskModal as HelpdeskModal };
